import React, { useMemo } from 'react';
import { buildAppointmentsDailyRatio } from '../dashboardStatUtils';
import AppointmentsRatioChart from './charts/AppointmentsRatioChart';

const formatCount = (value) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value || 0);

function DashboardAppointmentsRatio({ getStat, isLoading, loadError, lastUpdated }) {
    const ratio = useMemo(() => buildAppointmentsDailyRatio(getStat, 30), [getStat]);
    const hasData = ratio.summary.total > 0;

    const chartData = useMemo(
        () =>
            ratio.points.map((point) => ({
                day: point.label,
                total: point.total,
                confirmed: point.confirmed,
            })),
        [ratio.points]
    );

    const confirmationRate =
        ratio.summary.total > 0
            ? Math.round((ratio.summary.confirmed / ratio.summary.total) * 100)
            : 0;

    return (
        <section className="ecx-page-card ecx-page-panel dashboard-stats-panel dashboard-panel-theme dashboard-panel-theme--orange">
            <div className="ecx-page-panel__header">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="dashboard-stats-panel__heading">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-graph-up" />
                        </span>
                        <div>
                            <h2 className="dashboard-stats-panel__title">Appointments Ratio</h2>
                            <p className="dashboard-stats-panel__subtitle mb-0">
                                {lastUpdated
                                    ? `Daily total and confirmed · Last 30 days · Updated ${lastUpdated}`
                                    : 'Daily total and confirmed appointments — last 30 days'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="ecx-page-panel__body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-exclamation-circle me-2" />
                        {loadError}
                    </div>
                )}

                {isLoading ? (
                    <div className="dashboard-ratio">
                        <div className="dashboard-ratio__summary">
                            {Array.from({ length: 3 }).map((_, index) => (
                                <div
                                    key={`ratio-summary-skel-${index}`}
                                    className="dashboard-ratio__summary-card dashboard-stat-tile--skeleton"
                                />
                            ))}
                        </div>
                        <div
                            className="dashboard-stat-tile--skeleton"
                            style={{ minHeight: '24rem', borderRadius: '0.75rem' }}
                        />
                    </div>
                ) : (
                    <div className="dashboard-ratio">
                        <div className="dashboard-ratio__summary">
                            <div className="dashboard-ratio__summary-card">
                                <span className="dashboard-ratio__summary-label">Total appointments</span>
                                <span className="dashboard-ratio__summary-value">
                                    {formatCount(ratio.summary.total)}
                                </span>
                            </div>
                            <div className="dashboard-ratio__summary-card dashboard-ratio__summary-card--success">
                                <span className="dashboard-ratio__summary-label">Confirmed</span>
                                <span className="dashboard-ratio__summary-value">
                                    {formatCount(ratio.summary.confirmed)}
                                </span>
                            </div>
                            <div className="dashboard-ratio__summary-card">
                                <span className="dashboard-ratio__summary-label">Confirmation rate</span>
                                <span className="dashboard-ratio__summary-value">{confirmationRate}%</span>
                            </div>
                        </div>

                        {!hasData ? (
                            <div className="dashboard-empty-state">
                                <i className="bi bi-calendar3" />
                                <h5 className="mb-1 text-dark">No appointment ratio data yet</h5>
                                <p className="mb-0 small text-muted">
                                    Daily totals refresh with the clinic dashboard stats job.
                                </p>
                            </div>
                        ) : (
                            <AppointmentsRatioChart data={chartData} />
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

export default DashboardAppointmentsRatio;
